<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-5xl mx-auto px-4 pt-24 pb-6">

      <!-- VOLTAR -->
      <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-500 transition-colors mb-6 group">
        <v-icon small color="inherit" class="group-hover:-translate-x-1 transition-transform">mdi-arrow-left</v-icon>
        <span class="text-[10px] font-bold uppercase tracking-widest">Voltar para listagem</span>
      </button>

      <v-form ref="form" v-model="valid" lazy-validation>
        <div class="space-y-6 pb-24">

          <!-- SEÇÃO 1: DADOS PESSOAIS E FOTO -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-account-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DADOS PESSOAIS E FOTO</h2>
            </div>

            <div class="p-6">
              <div class="flex flex-col lg:flex-row gap-6">
                <!-- FOTO -->
                <div class="flex flex-col items-center gap-2 shrink-0">
                  <div
                    class="w-36 h-36 rounded-lg border-2 border-slate-200 bg-slate-100 overflow-hidden cursor-pointer hover:border-orange-400 transition-colors flex items-center justify-center"
                    @click="$refs.fileInput.click()"
                  >
                    <img
                      v-if="data.driver_photo"
                      :src="'https://api.sigfrotagov.com.br/uploads/' + data.driver_photo"
                      class="w-full h-full object-cover"
                    />
                    <v-icon v-else color="#cbd5e0" size="48">mdi-account</v-icon>
                  </div>
                  <input type="file" accept="image/jpeg, image/png" @change="uploadImage" ref="fileInput" style="display:none" />
                  <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-orange-500 transition-colors" @click="$refs.fileInput.click()">CLIQUE PARA ALTERAR A FOTO</span>
                </div>

                <!-- CAMPOS -->
                <div class="flex-1">
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <label class="field-label">CÓDIGO</label>
                      <v-text-field v-model="data.driver_cod" placeholder="0023" outlined dense hide-details class="field-input" />
                    </v-col>
                    <v-col cols="12" md="9">
                      <label class="field-label">NOME COMPLETO</label>
                      <v-text-field v-model="data.driver_name" placeholder="LEO JOSÉ" outlined dense hide-details class="field-input" />
                    </v-col>
                    <v-col cols="12">
                      <label class="field-label">PREFEITURA</label>
                      <v-autocomplete
                        v-model="data.prefecture_id"
                        :items="prefectures"
                        item-text="prefecture_social_name"
                        item-value="prefecture_id"
                        :disabled="userGroup != 1"
                        placeholder="PREFEITURA MUNICIPAL DE ASSUNÇÃO"
                        outlined dense hide-details
                        class="field-input"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <label class="field-label">SEXO</label>
                      <v-select v-model="data.driver_genre" :items="['Masculino', 'Feminino', 'Outros']" placeholder="SELECIONE..." outlined dense hide-details class="field-input" />
                    </v-col>
                    <v-col cols="12" md="4">
                      <label class="field-label">DATA NASCIMENTO</label>
                      <v-text-field v-model="data.driver_birth" v-mask-date.br placeholder="DD/MM/AAAA" outlined dense hide-details class="field-input" />
                    </v-col>
                    <v-col cols="12" md="4">
                      <label class="field-label">ESTADO CIVIL</label>
                      <v-select v-model="data.driver_marital_status" :items="['Casado', 'Solteiro', 'Viúvo', 'União Estável']" placeholder="SELECIONE..." outlined dense hide-details class="field-input" />
                    </v-col>
                    <v-col cols="12">
                      <label class="field-label">NÍVEL DE FORMAÇÃO</label>
                      <v-select v-model="data.driver_education_level" :items="['Fundamental Incompleto', 'Fundamental Completo', 'Ensino Médio Incompleto', 'Ensino Médio Completo', 'Ensino Superior Incompleto', 'Ensino Superior Completo', 'Mestrado', 'Doutorado']" placeholder="SELECIONE..." outlined dense hide-details class="field-input" />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </section>

          <!-- SEÇÃO 2: DOCUMENTAÇÃO E CNH -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-card-account-details-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DOCUMENTAÇÃO E CNH</h2>
            </div>

            <div class="p-6">
              <v-row dense>
                <v-col cols="12" md="4">
                  <label class="field-label">CPF</label>
                  <v-text-field v-model="data.driver_cpf" v-mask-cpf placeholder="000.000.000-00" outlined dense hide-details class="field-input" />
                  <span v-if="data.driver_cpf && !validCpf(data.driver_cpf)" class="text-[9px] text-red-500 font-bold uppercase mt-1 block">CPF Inválido</span>
                </v-col>
                <v-col cols="12" md="8">
                  <label class="field-label">IDENTIDADE / ÓRGÃO EMISSOR / UF</label>
                  <v-text-field v-model="data.driver_identidade" placeholder="EX: 00.000.000-0 SSP/PB" outlined dense hide-details class="field-input" />
                </v-col>

                <v-col cols="12" md="4">
                  <label class="field-label">Nº REGISTRO (CNH)</label>
                  <v-text-field v-model="data.driver_cnh_registro" placeholder="1234567890" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="4">
                  <label class="field-label">CATEGORIA (CNH)</label>
                  <v-select v-model="data.driver_category" :items="['A', 'B', 'C', 'D', 'E', 'AB', 'AC', 'AD', 'AE']" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="4">
                  <label class="field-label">1ª HABILITAÇÃO (CNH)</label>
                  <v-text-field v-model="data.driver_first_habilitation" v-mask-date.br placeholder="DD/MM/AAAA" outlined dense hide-details class="field-input" />
                </v-col>

                <v-col cols="12" md="6">
                  <label class="field-label">DATA EMISSÃO (CNH)</label>
                  <v-text-field v-model="data.driver_date_emission" v-mask-date.br placeholder="DD/MM/AAAA" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="6">
                  <label class="field-label">DATA VALIDADE (CNH)</label>
                  <v-text-field v-model="data.driver_date_validity" v-mask-date.br placeholder="DD/MM/AAAA" outlined dense hide-details class="field-input" />
                </v-col>
              </v-row>
            </div>
          </section>

          <!-- SEÇÃO 3: LOCALIZAÇÃO E CONTATO -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-map-marker-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">LOCALIZAÇÃO E CONTATO</h2>
            </div>

            <div class="p-6">
              <v-row dense>
                <v-col cols="12" md="6">
                  <label class="field-label">E-MAIL</label>
                  <v-text-field v-model="data.driver_mail" type="email" placeholder="usuario@email.com" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="3">
                  <label class="field-label">CELULAR / WHATSAPP</label>
                  <v-text-field v-model="data.driver_cel" v-mask-phone.br placeholder="(00) 00000-0000" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="3">
                  <label class="field-label">TELEFONE FIXO</label>
                  <v-text-field v-model="data.driver_tel" v-mask-phone.br placeholder="(00) 0000-0000" outlined dense hide-details class="field-input" />
                </v-col>

                <v-col cols="12" md="3">
                  <label class="field-label">CEP</label>
                  <v-text-field v-model="data.driver_postal_code" v-mask-cep placeholder="00000-000" outlined dense hide-details class="field-input" @blur="getCep(data.driver_postal_code)">
                    <template v-slot:append v-if="loader">
                      <v-progress-circular indeterminate size="16" width="2" color="orange" />
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="7">
                  <label class="field-label">LOGRADOURO</label>
                  <v-text-field v-model="data.driver_street" placeholder="AV. PRINCIPAL" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="2">
                  <label class="field-label">NÚMERO</label>
                  <v-text-field v-model="data.driver_number" placeholder="S/N" outlined dense hide-details class="field-input" />
                </v-col>

                <v-col cols="12" md="4">
                  <label class="field-label">BAIRRO</label>
                  <v-text-field v-model="data.driver_district" placeholder="CENTRO" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="6">
                  <label class="field-label">CIDADE</label>
                  <v-text-field v-model="data.driver_city" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="2">
                  <label class="field-label">UF</label>
                  <v-text-field v-model="data.driver_state" outlined dense hide-details class="field-input" maxlength="2" />
                </v-col>
              </v-row>
            </div>
          </section>

          <!-- SEÇÃO 4: ACESSO AO SISTEMA -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-security</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">SEGURANÇA E ACESSO</h2>
            </div>

            <div class="p-6">
              <v-row dense>
                <v-col cols="12" md="6">
                  <label class="field-label">LOGIN DE ACESSO (CPF)</label>
                  <v-text-field v-model="data.driver_login" readonly outlined dense hide-details class="field-input" prepend-inner-icon="mdi-account-outline" />
                </v-col>
                <v-col cols="12" md="6">
                  <label class="field-label">SENHA DE ACESSO</label>
                  <v-text-field v-model="data.driver_password" type="password" placeholder="MÍN. 6 CARACTERES" outlined dense hide-details class="field-input" prepend-inner-icon="mdi-lock-outline" />
                </v-col>

                <v-col cols="12" md="6">
                  <div
                    :class="['mt-2 p-4 rounded-lg border flex items-center justify-between cursor-pointer transition-all', data.driver_active ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200']"
                    @click="data.driver_active = !data.driver_active"
                  >
                    <div class="flex items-center gap-3">
                      <v-icon :color="data.driver_active ? 'green' : 'red'" small>{{ data.driver_active ? 'mdi-account-check' : 'mdi-account-off' }}</v-icon>
                      <div>
                        <p class="text-[10px] font-bold uppercase tracking-widest mb-0" :class="data.driver_active ? 'text-green-700' : 'text-red-700'">Status da Conta</p>
                        <p class="text-[9px] text-slate-400 uppercase mb-0">{{ data.driver_active ? 'Acesso liberado' : 'Acesso bloqueado' }}</p>
                      </div>
                    </div>
                    <v-switch v-model="data.driver_active" color="green" inset hide-details class="mt-0" @click.stop />
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <div
                    :class="['mt-2 p-4 rounded-lg border flex items-center justify-between cursor-pointer transition-all', data.driver_independent_refuel_blocked ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200']"
                    @click="data.driver_independent_refuel_blocked = !data.driver_independent_refuel_blocked"
                  >
                    <div class="flex items-center gap-3">
                      <v-icon :color="data.driver_independent_refuel_blocked ? 'orange' : '#94a3b8'" small>mdi-fuel-off</v-icon>
                      <div>
                        <p class="text-[10px] font-bold uppercase tracking-widest mb-0" :class="data.driver_independent_refuel_blocked ? 'text-amber-700' : 'text-slate-500'">Bloqueio de Abastecimento</p>
                        <p class="text-[9px] text-slate-400 uppercase mb-0">{{ data.driver_independent_refuel_blocked ? 'Requer autorização' : 'Solicitação livre' }}</p>
                      </div>
                    </div>
                    <v-switch v-model="data.driver_independent_refuel_blocked" color="orange" inset hide-details class="mt-0" @click.stop />
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- FOOTER AÇÕES -->
            <div class="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end items-center gap-4" style="border-radius: 0 0 12px 12px;">
              <button
                type="button"
                @click="$router.back()"
                style="background: none; border: none; cursor: pointer; white-space: nowrap;"
                class="text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest transition-colors"
              >
                CANCELAR
              </button>
              <button
                type="button"
                @click="validate"
                :disabled="loader"
                style="white-space: nowrap; width: auto; flex-shrink: 0;"
                class="inline-flex items-center gap-2 bg-[#f37021] hover:bg-orange-600 active:scale-95 text-white font-bold text-[11px] uppercase tracking-widest px-5 py-2.5 rounded-lg shadow-sm transition-all disabled:opacity-60"
              >
                <v-icon v-if="loader" small class="animate-spin" color="white">mdi-loading</v-icon>
                <v-icon v-else size="14" color="white">mdi-check</v-icon>
                <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR MOTORISTA' }}</span>
              </button>
            </div>
          </section>

        </div>
      </v-form>

      <!-- SNACKBAR -->
      <v-snackbar v-model="snack.show" :color="snack.color" timeout="4000" top elevation="24" class="custom-snack">
        <div class="flex items-center gap-3">
          <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn dark icon v-bind="attrs" @click="snack.show = false"><v-icon small>mdi-close</v-icon></v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    prefectures: [],
    snack: { show: false, message: "", color: "success" },
    data: {
      driver_cod: "", driver_id: null, driver_name: "", driver_photo: "",
      driver_cpf: "", driver_birth: "", driver_genre: "", driver_education_level: "",
      driver_marital_status: "", driver_mail: "", driver_cel: "", driver_tel: "",
      driver_postal_code: "", driver_street: "", driver_number: "", driver_complement: "",
      driver_district: "", driver_city: "", driver_state: "", driver_login: "",
      driver_password: "", driver_first_habilitation: "", driver_date_emission: "",
      driver_date_validity: "", driver_identidade: "", driver_cnh_registro: "",
      driver_category: "", prefecture_id: null, driver_active: true,
      driver_independent_refuel_blocked: false,
    },
    userGroup: null,
  }),
  watch: {
    'data.driver_cpf': function(val) {
      this.data.driver_login = val ? val.replace(/\D/g, '').slice(0, 11) : '';
    }
  },
  created() {
    this.userGroup = this.$store.getters.getUserData.group_id;
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`drivers/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        if (this.data.driver_independent_refuel_blocked == null) this.data.driver_independent_refuel_blocked = false;
        this.$store.dispatch("setLoader", false);
      });
    } else {
      this.data.prefecture_id = this.$store.getters.getUserData.table_id;
    }
  },
  methods: {
    validCpf(cpf) {
      if (!cpf) return false;
      cpf = cpf.replace(/\D/g, "");
      if (cpf.length != 11 || /^(\d)\1{10}$/.test(cpf)) return false;
      var result = true;
      [9, 10].forEach(function (j) {
        var soma = 0, r;
        cpf.split(/(?=)/).splice(0, j).forEach(function (e, i) { soma += parseInt(e) * (j + 2 - (i + 1)); });
        r = soma % 11; r = r < 2 ? 0 : 11 - r;
        if (r != cpf.substring(j, j + 1)) result = false;
      });
      return result;
    },
    validate() {
      if (!this.data.driver_name || !this.data.prefecture_id) {
        this.snack = { show: true, color: "warning", message: "Preencha Nome e Prefeitura!" };
        return;
      }
      if (this.data.driver_name.trim().split(/\s+/).length < 2) {
        this.snack = { show: true, color: "warning", message: "Informe o nome completo!" };
        return;
      }
      if (this.data.driver_cpf && !this.validCpf(this.data.driver_cpf)) {
        this.snack = { show: true, color: "warning", message: "CPF inválido!" };
        return;
      }
      this.createOrUpdate();
    },
    createOrUpdate() {
      this.loader = true;
      const method = this.id ? 'put' : 'post';
      const url = this.id ? `/drivers/${this.id}` : '/drivers';
      this.$store.dispatch("setLoader", true);
      this.$http[method](url, this.data)
        .then((r) => {
          this.snack = { show: true, message: r.data.result || "Sucesso!", color: "success" };
          this.loader = false;
          this.$store.dispatch("setLoader", false);
          setTimeout(() => this.$router.push("/drivers-manager"), 1500);
        })
        .catch((e) => {
          this.snack = { show: true, message: e.message, color: "error" };
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    uploadImage(event) {
      if (!event.target.files.length) return;
      this.$store.dispatch("setLoader", true);
      const fd = new FormData();
      fd.append("image", event.target.files[0], event.target.files[0].name);
      this.$http.post("/upload", fd)
        .then((r) => { this.data.driver_photo = r.data.cover; this.$store.dispatch("setLoader", false); })
        .catch((e) => {
          this.snack = { show: true, message: "Erro no upload: " + e.message, color: "error" };
          this.$store.dispatch("setLoader", false);
        });
    },
    getCep(cep) {
      if (!cep || cep.length < 10) return;
      this.loader = true;
      this.$http.get("https://viacep.com.br/ws/" + cep.replace(/\D/g, "") + "/json/")
        .then((r) => {
          if (!r.data.erro) {
            this.data.driver_street = r.data.logradouro;
            this.data.driver_district = r.data.bairro;
            this.data.driver_city = r.data.localidade;
            this.data.driver_state = r.data.uf;
          } else {
            this.snack = { show: true, message: "CEP não encontrado!", color: "warning" };
          }
          this.loader = false;
        })
        .catch(() => { this.loader = false; });
    },
  },
};
</script>

<style scoped>
.field-label {
  display: block;
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.field-input >>> .v-input__control .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 6px !important;
}

.field-input >>> .v-input__control .v-input__slot fieldset {
  border-color: #e2e8f0 !important;
  border-width: 1px !important;
}

.field-input >>> .v-input__control .v-input__slot:hover fieldset {
  border-color: #f37021 !important;
}

.field-input >>> input,
.field-input >>> .v-select__selection {
  font-size: 13px !important;
  color: #334155 !important;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 10px !important;
}
</style>
